// Translation files for English and Korean
export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      experience: 'Experience',
      work: 'Portfolio',
      contact: 'Contact',
      resume: 'Resume',
      goToBlogs: 'Go To Blogs',
      backToCV: 'Back to CV',
      language: 'Language',
    },

    // Hero Section
    hero: {
      greeting: 'Hello world, my name is',
      name: 'Tung-Duong "John" Mai.',
      subtitle: 'I code and build software.',
      description:
        'I’m a software engineer based in the Seoul Metropolitan Area, South Korea, specializing in applying software principles—especially ML/AI—to develop useful real-world applications.',
      contactButton: 'Get In Touch',
    },

    // About Section
    about: {
      title: 'About Me',
      greeting:
        'Hello! I’m Tung-Duong Mai, a software engineer based in the Seoul Metropolitan Area, South Korea.',
      description1:
        'I enjoy working with software that interacts with the real world. My goal is to apply software principles—especially ML/AI—to build applications that improve people’s lives. I aim to create products that benefit millions of people.',
      description2:
        'I pursued my undergraduate studies at <a href="https://www.kaist.ac.kr/en/" target="_blank" rel="noopener noreferrer">KAIST</a>, double majoring in Computer Science and Electrical Engineering. I graduated with a B.S. degree, <i>magna cum laude</i>.',
      description3:
        'Shortly after graduation, I joined <a href="https://www.samsung.com/sec/" target="_blank" rel="noopener noreferrer">Samsung Electronics Co.</a> as a software engineer in the <a href="https://www.samsungcareers.com/subsid/detail/C10CAA?lang=en" target="_blank" rel="noopener noreferrer">Device eXperience</a> (DX) division, initially developing the <a href="https://www.samsung.com/us/tvs/smart-tv/airplay-apple-tv-app" target="_blank" rel="noopener noreferrer">AirPlay application</a> for Samsung Smart TVs, enabling seamless high-quality audio/video mirroring and casting from Apple devices. I am currently an AI Software Engineer in the AE AI Solution Lab, leveraging cutting-edge LLM technologies to build innovative AI features for <a href="https://www.harman.com/" target="_blank" rel="noopener noreferrer">Harman International</a>, a leading global audio and connected-car technology company and a Samsung American subsidiary.',
      technologies: 'Here are a few technologies I’ve been working with recently:',
    },

    // Projects Section
    projects: {
      title: 'Some Things I’ve Built',
      featured: 'Featured Project',
      viewProject: 'View Project',
      viewCode: 'View Code',
      other: 'Other Noteworthy Projects',
      exploreAll: 'Explore all projects',
      readWriting: 'Read related writing',
      showMore: 'Show More',
      showLess: 'Show Less',
    },

    // Blog Section
    blog: {
      title: 'Blog',
      subtitle: 'A mosaic of past endeavors',
      filterByTags: 'Filter by tags',
      projectWriteup: 'Project Writeup',
      technicalBlog: 'Tech Blog',
      personalBlog: 'Personal Blog',
      viewAllTags: 'View all tags',
      allMemories: 'All Blogs',
      taggedWith: 'Tags',
      home: 'Home',
      allBlogs: 'All blogs',
      previous: 'Previous',
      next: 'Next',
      seeAllPosts: 'See All Posts',
    },

    // Contact Section
    contact: {
      overline: 'What’s Next?',
      title: 'Get In Touch',
      description:
        'Although I’m not currently looking for new opportunities, my inbox is always open. Whether you have a question or just want to say hi, I’ll do my best to get back to you.',
      button: 'Say Hello',
    },

    // Jobs Section
    jobs: {
      title: 'Where I’ve Worked',
    },

    // Archive Section
    archive: {
      title: 'Archive',
      subtitle: 'A big list of things I’ve worked on',
      year: 'Year',
      projectTitle: 'Title',
      madeAt: 'Made at',
      builtWith: 'Built with',
      link: 'Link',
    },

    // Footer
    footer: {
      builtWith: 'Built with',
      and: 'and',
      hostedOn: 'Hosted on',
      builtBy: 'Built by Tung-Duong John Mai',
      templateBy: 'on the template of Brittany Chiang',
      copyright: '© 2024 Tung-Duong Mai. All rights reserved.',
    },

    // Common/Shared
    common: {
      loading: 'Loading...',
      error: 'Error',
      notFound: 'Not Found',
      goBack: 'Go Back',
      readMore: 'Read More',
      close: 'Close',
      open: 'Open',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      view: 'View',
      share: 'Share',
      download: 'Download',
      upload: 'Upload',
      submit: 'Submit',
      reset: 'Reset',
    },

    // SEO/Meta
    meta: {
      title: 'Tung-Duong "John" Mai - Software Engineer',
      description:
        'Portfolio website of Tung-Duong Mai, a software engineer specializing in ML/AI applications.',
      keywords: 'software engineer, ML, AI, portfolio, developer',
      author: 'Tung-Duong Mai',
      siteName: 'John Mai Portfolio',
    },
  },

  ko: {
    // Navigation
    nav: {
      home: '홈',
      about: '소개',
      experience: '경력',
      work: '포트폴리오',
      contact: '연락처',
      resume: '이력서',
      goToBlogs: '블로그 보기',
      backToCV: 'CV로 돌아가기',
      language: '언어',
    },

    // Hero Section
    hero: {
      greeting: 'Hello world,',
      name: 'Tung-Duong "John" Mai입니다.',
      subtitle: '소프트웨어를 만듭니다.',
      description:
        '수도권을 기반으로 활동하는 소프트웨어 엔지니어로서, 실제 세계에서 유용한 애플리케이션을 개발하기 위해 ML/AI를 포함한 소프트웨어 원칙을 적용하는 데 전문성을 가지고 있습니다.',
      contactButton: '연락하기',
    },

    // About Section
    about: {
      title: '자기소개',
      greeting: '안녕하세요! 저는 수도권에 기반한 소프트웨어 엔지니어 Tung-Duong Mai라고 합니다.',
      description1:
        '실제 세계와 상호작용하는 소프트웨어 작업을 즐깁니다. 제 목표는 소프트웨어 원칙, 특히 ML/AI를 적용하여 사람들의 삶을 편리하게 하는 애플리케이션을 구축하는 것입니다. 수백만 명의 사람들에게 혜택을 주는 제품을 만들고 싶습니다.',
      description2:
        '<a href="https://www.kaist.ac.kr/" target="_blank" rel="noopener noreferrer">한국과학기술원 (KAIST)</a>에서 컴퓨터공학과 전자및전기공학을 복수 전공하며 학부 과정을 마쳤습니다. 준우수등(magna cum laude)으로 학사 학위를 취득했습니다.',
      description3:
        '졸업 직후 <a href="https://www.samsung.com/sec/" target="_blank" rel="noopener noreferrer">삼성전자</a>의 <a href="https://www.samsungcareers.com/subsid/detail/C10CAA" target="_blank" rel="noopener noreferrer">Device eXperience</a>(DX) 부문에서 소프트웨어 엔지니어로 합류하여 Apple 기기에서 삼성 스마트 TV로 고품질 오디오·비디오 미러링 및 캐스팅을 지원하는 <a href="https://www.samsung.com/sec/tvs/smart-tv/airplay-apple-tv-app" target="_blank" rel="noopener noreferrer">AirPlay 애플리케이션</a> 개발했습니다. 현재는 AE AI Solution Lab에서 AI 소프트웨어 엔지니어로 근무하며, 최신 LLM기술을 활용해 글로벌 오디오·커넥티드카 기술 기업이자 삼성 계열사인 <a href="https://www.harman.com/" target="_blank" rel="noopener noreferrer">Harman International</a>의 비즈니스 포트폴리오를 위한 혁신적인 AI 기능을 개발하고 있습니다.',
      technologies: '최근에 작업한 몇 가지 기술입니다:',
    },

    // Projects Section
    projects: {
      title: '제가 만든 것들',
      featured: '주요 프로젝트',
      viewProject: '프로젝트 보기',
      viewCode: '코드 보기',
      other: '기타 주목할 만한 프로젝트',
      exploreAll: '모든 프로젝트 탐색하기',
      readWriting: '관련 글 읽기',
      showMore: '더보기',
      showLess: '접기',
    },

    // Blog Section
    blog: {
      title: '블로그',
      subtitle: '과거 노력들의 모자이크',
      filterByTags: '태그로 필터링',
      projectWriteup: '프로젝트 정리',
      technicalBlog: '기술 블로그',
      personalBlog: '개인 블로그',
      viewAllTags: '모든 태그 보기',
      allMemories: '모든 블로그',
      taggedWith: '태그',
      home: '홈',
      allBlogs: '모든 블로그',
      previous: '이전',
      next: '다음',
      seeAllPosts: '모든 블로그 보기',
    },

    // Contact Section
    contact: {
      overline: 'What’s Next?',
      title: '연락하기',
      description:
        '현재 새로운 기회를 찾고 있지는 않지만, 언제든지 연락 주시면 성실히 답변드리겠습니다.',
      button: '연락하기',
    },

    // Jobs Section
    jobs: {
      title: '제가 일한 곳',
    },

    // Archive Section
    archive: {
      title: '아카이브',
      subtitle: '제가 참여했던 작업들을 정리한 목록',
      year: '연도',
      projectTitle: '제목',
      madeAt: '만든 곳',
      builtWith: '사용한 기술',
      link: '링크',
    },

    // Footer
    footer: {
      builtWith: '제작:',
      and: '그리고',
      hostedOn: '호스팅:',
      builtBy: '제작자: Tung-Duong John Mai',
      templateBy: 'Brittany Chiang 템플릿 기반',
      copyright: '© 2024 Tung-Duong Mai. 모든 권리 보유.',
    },

    // Common/Shared
    common: {
      loading: '로딩 중...',
      error: '오류',
      notFound: '찾을 수 없음',
      goBack: '돌아가기',
      readMore: '더 읽기',
      close: '닫기',
      open: '열기',
      cancel: '취소',
      confirm: '확인',
      save: '저장',
      edit: '편집',
      delete: '삭제',
      search: '검색',
      filter: '필터',
      sort: '정렬',
      view: '보기',
      share: '공유',
      download: '다운로드',
      upload: '업로드',
      submit: '제출',
      reset: '초기화',
    },

    // SEO/Meta
    meta: {
      title: 'Tung-Duong "John" Mai - 소프트웨어 엔지니어',
      description:
        'ML/AI 애플리케이션 전문 소프트웨어 엔지니어 Tung-Duong Mai의 포트폴리오 웹사이트입니다.',
      keywords: '소프트웨어 엔지니어, ML, AI, 포트폴리오, 개발자',
      author: 'Tung-Duong Mai',
      siteName: 'John Mai 포트폴리오',
    },
  },
};

// Helper function
export const getTranslation = (language, keys) => {
  const keysArray = keys.split('.');
  let value = translations[language];

  for (const key of keysArray) {
    value = value?.[key];
  }

  return value || keys;
};
